import { useCallback, useEffect, useRef } from "react"

const DRAG_THRESHOLD = 3

/**
 * Hook that enables horizontal drag-to-scroll on desktop.
 * Clicking and dragging inside a horizontally scrollable container
 * will scroll it, mimicking touch-scroll behavior on desktop.
 *
 * - Listens on `window` for mousemove/mouseup so dragging works
 *   even when the cursor leaves the container.
 * - Prevents native link/image drag and accidental click navigation.
 * - Only activates on primary mouse button (left click).
 *
 * Usage:
 *   const dragScroll = useDragScroll()
 *   <div {...dragScroll} className="overflow-x-auto cursor-grab">
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null)
  const state = useRef({
    dragging: false,
    startX: 0,
    scrollLeft: 0,
    moved: false,
  })

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return // only primary button
    const el = ref.current
    if (!el) return

    state.current = {
      dragging: true,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
      moved: false,
    }
    document.body.style.cursor = "grabbing"
  }, [])

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const onMouseMove = (e: MouseEvent) => {
      const s = state.current
      if (!s.dragging) return

      const dx = e.clientX - s.startX
      if (Math.abs(dx) > DRAG_THRESHOLD) s.moved = true
      if (s.moved) {
        e.preventDefault()
        el.scrollLeft = s.scrollLeft - dx
      }
    }

    const onMouseUp = () => {
      const s = state.current
      if (!s.dragging) return
      s.dragging = false
      document.body.style.cursor = ""

      if (s.moved) {
        // Prevent the next click so links/buttons don't fire after a drag
        const stop = (e: Event) => {
          e.preventDefault()
          e.stopPropagation()
        }
        el.addEventListener("click", stop, { capture: true, once: true })
      }
    }

    const onDragStart = (e: DragEvent) => {
      // Prevent native link/image drag only while actively dragging
      if (state.current.dragging) e.preventDefault()
    }

    // Window-level listeners so drag continues outside the element
    window.addEventListener("mousemove", onMouseMove)
    window.addEventListener("mouseup", onMouseUp)
    el.addEventListener("dragstart", onDragStart)

    return () => {
      window.removeEventListener("mousemove", onMouseMove)
      window.removeEventListener("mouseup", onMouseUp)
      el.removeEventListener("dragstart", onDragStart)
    }
  }, [])

  return { ref, onMouseDown }
}
